<script setup lang="ts">
import { computed, ref, storeToRefs, useForm } from '#imports';
import { toTypedSchema } from '@vee-validate/yup';
import * as Yup from 'yup';

import { errorValidationMessages } from '@/app/messages';
import { Validation } from '@/app/rules';

import type { AuthTypes } from './../types';
import { userSignService } from './../api';
import { useUserDialogStore } from './../store/dialog';
import { useUserAuthStore } from './../store/auth';

import { useDialogResponseStore } from '@/widgets/dialog-response/store';

import { removeKeyObject } from '@/shared/utils/remove-key-object';

const dialogResponseStore = useDialogResponseStore();
const { setResponseModalMessage, showResponseModal } = dialogResponseStore;

const { closeUserDialog } = useUserDialogStore();

const props = withDefaults(defineProps<{
	modelValue: boolean;
}>(), {
	modelValue: false,
});

const emit = defineEmits<{
	(e: 'update:modelValue'): void;
}>();

const value = computed({
	get: () => props.modelValue,
	set: () => emit('update:modelValue'),
});

/** Form */
const { defineField, handleSubmit, handleReset, errors } = useForm({
	validationSchema: toTypedSchema(
		Yup.object().shape({
			password: Yup.string()
				.min(Validation.Password.minLength, errorValidationMessages.regex.password)
				.required(errorValidationMessages.required.password),
			passwordRepeat: Yup.string()
				.required(errorValidationMessages.required.passwordRepeat)
				.oneOf([Yup.ref('password')], errorValidationMessages.sameAs.passwordRepeat),
		}),
	),
});

const [passwordModel, passwordAttrs] = defineField('password');
const [passwordRepeatModel, passwordRepeatAttrs] = defineField('passwordRepeat');

const isLoading = ref(false);
const errorResponse = ref<null | Record<string, Array<string>>>(null);

const userAuthStore = useUserAuthStore();
const { changePasswordToken } = storeToRefs(userAuthStore);

const onSubmit = handleSubmit(async (model) => {
	errorResponse.value = null;
	isLoading.value = true;

	const payload = removeKeyObject(model, (key: any) => key === 'passwordRepeat');

	const response = await userSignService.changePasswordConfirm({
		...payload,
		token: changePasswordToken.value,
	} as AuthTypes.ChangePasswordConfirmPayload);

	isLoading.value = false;

	if ('error' in response) {
		errorResponse.value = response.error;
		return;
	}

	handleReset();
	setResponseModalMessage(response.detail);
	setTimeout(() => showResponseModal(), 0);
	closeUserDialog();
});
</script>

<template>
	<v-dialog v-model="value">
		<div class="dialog-user-recovery-password">
			<h2 class="dialog-user-recovery-password__title mb-24">
				Восстановление пароля
			</h2>

			<form class="form" @submit.prevent="onSubmit">
				<div class="form__block">
					<div class="form__field">
						<v-input
							v-model="passwordModel"
							v-bind="passwordAttrs"
							type="password"
							placeholder="Пароль"
							name="PASSWORD"
							required
							:is-error="!!errors.password"
							:error-message="errors.password"
						/>
					</div>

					<div class="form__field">
						<v-input
							v-model="passwordRepeatModel"
							v-bind="passwordRepeatAttrs"
							type="password"
							placeholder="Повторите Пароль"
							name="CONFIRMATION_PASSWORD"
							required
							:is-error="!!errors.passwordRepeat"
							:error-message="errors.passwordRepeat"
						/>
					</div>
				</div>

				<div class="form__block">
					<v-button type="submit" class="form__submit">
						Восстановить пароль
					</v-button>
				</div>

				<div
					v-if="errorResponse !== null"
					class="form__block form__block--error"
				>
					<p
						v-for="(error, key) in errorResponse"
						:key="key"
						class="text-small mb-8"
					>
						{{ error }}
					</p>
				</div>
			</form>
		</div>
	</v-dialog>
</template>
